import { makeAutoObservable } from "mobx"
import React from "react"

import {
    AdminService,
    TenantAdminService,
    avy_api_pkg_common_LeaseContractDetails,
} from "src/api"
import { createLoadingKeys } from "src/lib/loading"
import { parseDate } from "src/lib/date"
import { loads } from "src/channel/utils"

import { reportUnhandledApiError } from "src/lib/report"
import { IContractDetailsWithDateObjects } from "src/views/contracts/pages/details/types/contract-detail"

//#region Context and Loading Keys
export class ContractDetailStore {
    static Context = React.createContext<ContractDetailStore | null>(null)
    static LoadingKeys = createLoadingKeys("init")
    //#endregion

    //#region State
    id: number | null = null
    contract: IContractDetailsWithDateObjects | null = null
    useMockData: boolean = false
    recordNotFound: boolean = false
    //#endregion

    //#region Constructor
    constructor() {
        makeAutoObservable(this)
    }
    //#endregion

    //#region Initialization and Data Loading
    @loads(() => ContractDetailStore.LoadingKeys.init)
    async init(id: number) {
        this.id = id
        this.recordNotFound = false

        try {
            await this.loadContract()
        } catch (error) {
            reportUnhandledApiError(error)
        }
    }

    async loadContract() {
        if (this.id == null) return

        try {
            const response = await AdminService.getV1AdminLeaseContractDetails({
                leaseContractId: this.id,
            })

            if (response != null) {
                this.mapContractData(response)
                this.recordNotFound = !Boolean(response.lease_contract_id)
            } else {
                this.recordNotFound = true
            }
        } catch (error) {
            this.recordNotFound = true
            reportUnhandledApiError(error)
        }
    }
    //#endregion

    //#region Data Mapping
    private mapContractData(
        contractData: avy_api_pkg_common_LeaseContractDetails,
    ) {
        this.contract = {
            lease_contract_id: contractData.lease_contract_id,
            agreement_no: contractData.agreement_no,
            annual_rent: contractData.annual_rent,
            external_id: contractData.external_id,
            created_date:
                contractData.created_date != null
                    ? parseDate(contractData.created_date)
                    : undefined,
            lease_from:
                contractData.lease_from != null
                    ? parseDate(contractData.lease_from)
                    : undefined,
            lease_to:
                contractData.lease_to != null
                    ? parseDate(contractData.lease_to)
                    : undefined,
            legal_entity_id: contractData.legal_entity_id ?? undefined,
            monthly_rent: contractData.monthly_rent,
            legal_entity: contractData.legal_entity ?? undefined,
            property_object_id: contractData.property_object_id ?? undefined,
            status: contractData?.status ?? "ACTIVE",
            object_id: contractData.object_id,
            property_owner: contractData.property_owner,
            tenants:
                contractData.tenants?.map((tenant) => ({
                    tenant_id: tenant.tenant_id ?? undefined,
                    name: tenant.name ?? "-",
                    email: tenant.email ?? "-",
                    phone_number: tenant.phone_number ?? "-",
                    role: tenant.role ?? "-",
                    personal_id: tenant.personal_id ?? "-",
                    tenant_external_id: tenant.tenant_external_id ?? "-",
                })) ?? [],
        }
    }
    //#endregion

    //#region User Simulation
    async simulateUser(tenantId?: number) {
        try {
            if (tenantId == null) return
            const propertyObjectId = this.contract?.property_object_id ?? 0

            const response =
                await TenantAdminService.getV1AdminTenantsMagicLink({
                    tenantId,
                    propertyObjectId,
                })

            const { link } = response
            if (link !== undefined) {
                window.open(link, "_blank")
            }
        } catch (error) {
            reportUnhandledApiError(error)
        }
    }
    //#endregion

    //#region Cleanup
    dispose() {
        this.contract = null
        this.recordNotFound = false
    }
    //#endregion
}
